import { supabase } from '@/lib/supabase';

export interface IntakeLead {
  id: string;
  contact_first_name: string | null;
  contact_last_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  address: string | null;
  bedrooms: string | null;
  bathrooms: string | null;
  square_footage: string | null;
  property_type: string | null;
  frequency: string;
  monthly_rate: number;
  lead_status: string;
  created_at: string;
  submitted_at: string | null;
  admin_approved_at: string | null;
  admin_approved_by: string | null;
}

/**
 * Fetches ALL intake leads directly from Supabase.
 * No caching, no local state — always reflects DB truth.
 */
export async function getAllIntakeLeads(): Promise<{ data: IntakeLead[] | null; error: any }> {
  try {
    console.log('[intakeLeadsService] Fetching all leads from Supabase...');

    const { data, error } = await supabase
      .from('intake_leads')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[intakeLeadsService] SELECT error:', error);
      return { data: null, error };
    }

    console.log(`[intakeLeadsService] Fetched ${data?.length ?? 0} leads. Statuses:`, 
      data?.reduce((acc: Record<string, number>, l) => {
        acc[l.lead_status] = (acc[l.lead_status] || 0) + 1;
        return acc;
      }, {})
    );

    return { data, error: null };
  } catch (err: any) {
    console.error('[intakeLeadsService] Unexpected fetch error:', err);
    if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
      return {
        data: null,
        error: { message: 'Cannot connect to database. Your Supabase project may be paused or inactive.' }
      };
    }
    return { data: null, error: err };
  }
}

/**
 * Updates lead_status to 'approved' directly in Supabase.
 * Logs the full response for debugging.
 * Returns error if update fails — caller must NOT update UI until this succeeds.
 */
export async function approveIntakeLead(leadId: string, adminId: string | null): Promise<{ error: any }> {
  try {
    console.log(`[intakeLeadsService] Approving lead ${leadId}...`);

    const { data, error, status, statusText } = await supabase
      .from('intake_leads')
      .update({
        lead_status: 'approved',
        admin_approved_at: new Date().toISOString(),
        admin_approved_by: adminId || null
      })
      .eq('id', leadId)
      .select();

    console.log(`[intakeLeadsService] Approve response — status: ${status} ${statusText}`, { data, error });

    if (error) {
      console.error('[intakeLeadsService] Approve UPDATE error:', error);
      return { error };
    }

    if (!data || data.length === 0) {
      console.warn('[intakeLeadsService] Approve UPDATE returned no rows. Check RLS policies or lead ID.');
      return { error: { message: 'Update returned no rows. The record may not exist or RLS is blocking the update.' } };
    }

    console.log(`[intakeLeadsService] Lead ${leadId} approved successfully. DB record:`, data[0]);
    return { error: null };
  } catch (err: any) {
    console.error('[intakeLeadsService] Unexpected approve error:', err);
    if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
      return { error: { message: 'Cannot connect to database. Your Supabase project may be paused or inactive.' } };
    }
    return { error: err };
  }
}

/**
 * Updates lead_status to 'rejected' directly in Supabase.
 * Logs the full response for debugging.
 * Returns error if update fails — caller must NOT update UI until this succeeds.
 */
export async function rejectIntakeLead(leadId: string, adminId: string | null): Promise<{ error: any }> {
  try {
    console.log(`[intakeLeadsService] Rejecting lead ${leadId}...`);

    const { data, error, status, statusText } = await supabase
      .from('intake_leads')
      .update({
        lead_status: 'rejected',
        admin_approved_at: new Date().toISOString(),
        admin_approved_by: adminId || null
      })
      .eq('id', leadId)
      .select();

    console.log(`[intakeLeadsService] Reject response — status: ${status} ${statusText}`, { data, error });

    if (error) {
      console.error('[intakeLeadsService] Reject UPDATE error:', error);
      return { error };
    }

    if (!data || data.length === 0) {
      console.warn('[intakeLeadsService] Reject UPDATE returned no rows. Check RLS policies or lead ID.');
      return { error: { message: 'Update returned no rows. The record may not exist or RLS is blocking the update.' } };
    }

    console.log(`[intakeLeadsService] Lead ${leadId} rejected successfully. DB record:`, data[0]);
    return { error: null };
  } catch (err: any) {
    console.error('[intakeLeadsService] Unexpected reject error:', err);
    if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
      return { error: { message: 'Cannot connect to database. Your Supabase project may be paused or inactive.' } };
    }
    return { error: err };
  }
}

/**
 * Creates a new intake lead for anonymous users.
 * No authentication required — uses anon RLS policy.
 */
export async function createIntakeLead(leadData: {
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  address: string;
  bedrooms: string;
  bathrooms: string;
  square_footage: string;
  property_type: string;
  frequency: string;
  monthly_rate: number;
  service_type: string;
}): Promise<{ data: IntakeLead | null; error: any }> {
  try {
    const { data, error } = await supabase
      .from('intake_leads')
      .insert([{
        ...leadData,
        lead_status: 'pending',
        submitted_at: new Date().toISOString(),
      }])
      .select()
      .single();

    if (error) {
      return { data: null, error };
    }

    return { data, error: null };
  } catch (err: any) {
    if (err?.message?.includes('Failed to fetch') || err?.message?.includes('NetworkError')) {
      return {
        data: null,
        error: { message: 'Cannot connect to database. Your Supabase project may be paused or inactive.' }
      };
    }
    return { data: null, error: err };
  }
}